/**
 * Persists opened walks on-device (IndexedDB) so they can be reopened
 * offline without re-picking the file - including when the original file
 * is no longer reachable (deleted, or an iCloud-only placeholder that
 * would need network to re-download through the Files picker).
 *
 * Deliberately raw IndexedDB, not Dexie: the only things ever stored are
 * ZIP blobs plus a few fields, so a query library buys nothing here.
 */

export interface StoredWalk {
  id: string;
  name: string;
  fileSize: number;
  importedAt: number;
  lastOpenedAt: number | null;
  /** The original picked file's bytes, verbatim. Re-parsed on open (cheap: central directory only). */
  blob: Blob;
  /** Graphics-memory estimate in MB (walk.json `gpu_mb`, or export_report.json), when the walk has one. */
  gpuMb?: number;
  /** walk.json `gpu_mb_uncompressed`: the estimate from the original files, when the walk has compressed textures. */
  gpuMbUncompressed?: number;
  /** Walk Bake export preset from walk.json, e.g. "tablet". */
  preset?: string;
}

export type StoredWalkSummary = Omit<StoredWalk, "blob">;

export interface StorageUsage {
  usageBytes: number;
  quotaBytes: number | null;
  persisted: boolean;
}

const DB_NAME = "walk-player";
const DB_VERSION = 2;
/** Small metadata records (StoredWalkSummary). Listing and renaming only ever touch these. */
const META = "walks";
/** The ZIP bytes, one record per walk, written once and never rewritten. */
const FILES = "files";

interface FileRecord {
  id: string;
  blob: Blob;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const tx = req.transaction as IDBTransaction;
      const meta = event.oldVersion >= 1 ? tx.objectStore(META) : db.createObjectStore(META, { keyPath: "id" });
      const files = db.createObjectStore(FILES, { keyPath: "id" });
      if (event.oldVersion === 1) {
        // Version 1 kept the blob inside the metadata record. Move it out, so that
        // touching metadata never rewrites the whole file (see openStoredWalk).
        meta.openCursor().onsuccess = (e) => {
          const cursor = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
          if (!cursor) return;
          const { blob, ...summary } = cursor.value as StoredWalkSummary & { blob?: Blob };
          if (blob) files.put({ id: summary.id, blob } satisfies FileRecord);
          cursor.update(summary);
          cursor.continue();
        };
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Could not open local storage."));
  });
}

/** Runs fn in one transaction over the given stores; resolves once it has committed. */
async function inTransaction<T>(
  stores: string[],
  mode: IDBTransactionMode,
  fn: (tx: IDBTransaction) => IDBRequest<T> | void,
): Promise<T | undefined> {
  const db = await openDb();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(stores, mode);
      const request = fn(tx);
      tx.oncomplete = () => resolve(request ? request.result : undefined);
      tx.onerror = () => reject(tx.error ?? new Error("Local storage request failed."));
      tx.onabort = () => reject(tx.error ?? new Error("Local storage request was aborted."));
    });
  } finally {
    db.close();
  }
}

function toSummary(walk: StoredWalk): StoredWalkSummary {
  const { blob: _blob, ...summary } = walk;
  return summary;
}

/** Saves (or overwrites) a walk. Throws QuotaExceededError-ish errors as-is; callers decide the fallback. */
export async function putWalk(walk: StoredWalk): Promise<void> {
  await inTransaction([META, FILES], "readwrite", (tx) => {
    tx.objectStore(FILES).put({ id: walk.id, blob: walk.blob } satisfies FileRecord);
    tx.objectStore(META).put(toSummary(walk));
  });
}

export async function getWalkSummary(id: string): Promise<StoredWalkSummary | undefined> {
  return inTransaction<StoredWalkSummary | undefined>([META], "readonly", (tx) => tx.objectStore(META).get(id));
}

export async function getWalkBlob(id: string): Promise<Blob | undefined> {
  const file = await inTransaction<FileRecord | undefined>([FILES], "readonly", (tx) => tx.objectStore(FILES).get(id));
  return file?.blob;
}

export async function listWalks(): Promise<StoredWalkSummary[]> {
  const all = (await inTransaction<StoredWalkSummary[]>([META], "readonly", (tx) => tx.objectStore(META).getAll())) ?? [];
  return all.sort((a, b) => (b.lastOpenedAt ?? b.importedAt) - (a.lastOpenedAt ?? a.importedAt));
}

/** Updates metadata only: the stored file is never read or rewritten here. */
async function updateSummary(id: string, change: (walk: StoredWalkSummary) => void): Promise<void> {
  await inTransaction([META], "readwrite", (tx) => {
    const store = tx.objectStore(META);
    const request = store.get(id);
    request.onsuccess = () => {
      const walk = request.result as StoredWalkSummary | undefined;
      if (!walk) return;
      change(walk);
      store.put(walk);
    };
  });
}

/** Backfills the estimate of a walk saved by an older Player. Metadata only. */
export async function setWalkEstimate(
  id: string,
  gpuMb: number | undefined,
  gpuMbUncompressed: number | undefined,
  preset: string | undefined,
): Promise<void> {
  await updateSummary(id, (walk) => {
    if (gpuMb !== undefined) walk.gpuMb = gpuMb;
    if (gpuMbUncompressed !== undefined) walk.gpuMbUncompressed = gpuMbUncompressed;
    if (preset !== undefined) walk.preset = preset;
  });
}

export async function touchLastOpened(id: string): Promise<void> {
  await updateSummary(id, (walk) => {
    walk.lastOpenedAt = Date.now();
  });
}

export async function renameWalk(id: string, name: string): Promise<void> {
  await updateSummary(id, (walk) => {
    walk.name = name;
  });
}

export async function deleteWalk(id: string): Promise<void> {
  await inTransaction([META, FILES], "readwrite", (tx) => {
    tx.objectStore(META).delete(id);
    tx.objectStore(FILES).delete(id);
  });
}

export async function clearAllWalks(): Promise<void> {
  await inTransaction([META, FILES], "readwrite", (tx) => {
    tx.objectStore(META).clear();
    tx.objectStore(FILES).clear();
  });
}

export async function getStorageUsage(): Promise<StorageUsage> {
  const persisted = await isStoragePersisted();
  if (navigator.storage?.estimate) {
    const estimate = await navigator.storage.estimate();
    return { usageBytes: estimate.usage ?? 0, quotaBytes: estimate.quota ?? null, persisted };
  }
  const rows = await listWalks();
  return { usageBytes: rows.reduce((sum, w) => sum + w.fileSize, 0), quotaBytes: null, persisted };
}

async function isStoragePersisted(): Promise<boolean> {
  if (!navigator.storage?.persisted) return false;
  try {
    return await navigator.storage.persisted();
  } catch {
    return false;
  }
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
